import Http from '../../../../network/Http'
import {NatGetFacilityReserveBillingData, SearchConditionPageInfo} from './NatGetFacilityReserveBillingData'
import {
    NatPostFacilityReserveBillingData,
    NatPostFacilityReserveBillingDataParams,
} from './NatPostFacilityReserveBillingData'
import {ContentType} from '../../../../network/Constants'
import {SortOrder} from '../../../../components/shared/SortOrder'
import {
    NatGetFacilityReserveList,
    SearchCondition as FacilityReserveSearchCondition,
    SearchConditionPageInfo as FacilityReserveSearchConditionPageInfo,
} from './NatGetFacilityReserveList'
import {NatGetAdditionalBillingList, SearchCondition} from './NatGetAdditionalBillingList'
import {NatDeleteAdditionalBilling} from './NatDeleteAdditionalBilling'
import {NatGetAdditionalBilling} from './NatGetAdditionalBilling'
import {NatPostAdditionalBilling, NatPostAdditionalBillingParams} from './NatPostAdditionalBilling'
import {NatPutAdditionalBilling, NatPutAdditionalBillingParams} from './NatPutAdditionalBilling'
import {NatGetFacilityReserveDataOutputParams} from './NatGetFacilityReserveDataOutput'
import {NatGetFacilityReserve} from './NatGetFacilityReserve'
import {NatDeleteFacilityReserve} from './NatDeleteFacilityReserve'
import {NatPutFacilityReserveDuration, NatPutFacilityReserveDurationRequest} from './NatPutFacilityReserveDuration'
import {NatPutFacilityReserveOvernight, NatPutFacilityReserveOvernightRequest} from './NatPutFacilityReserveOvernight'
import {ApiResponse} from '../../../../network/response/ApiResponse'
import FacilityForReservation from '../../../../components/facilities/FacilityForReservation'
import {ReservationStatusCardInformation} from '../../../../components/reservations/AdditionalChargeCardInformation'
import ReservationCardInformation from '../../../../components/reservations/ReservationCardInformation'
import {
    NatPostClosedSettingParams,
    RegisterClosedSetting,
    ReservationsByClosedPeriod,
    UpdateClosedSetting,
} from '../../closedSetting/repository/NatPostClosedSetting'
import moment from 'moment'
import {PutBillingDataParams} from './NatPutBillingData'

export default class NatFacilityReserveRepo {
    http: Http

    constructor(http: Http) {
        this.http = http
    }

    // 請求データ一覧取得
    async getFacilityReserveBillingData(
        searchConditionPageInfo?: SearchConditionPageInfo): Promise<NatGetFacilityReserveBillingData> {
        const searchParams: string[] = []

        // 検索条件(ページ情報)
        if (searchConditionPageInfo) {
            if (searchConditionPageInfo.pageNum) {
                searchParams.push(`pageNum=${searchConditionPageInfo.pageNum}`)
            }
            if (searchConditionPageInfo.page) {
                searchParams.push(`page=${searchConditionPageInfo.page}`)
            }
        }

        const searchParamsStr: string = searchParams.join('&')
        return await this.http.get(`/api/admin/facilityReserve/billingData?${searchParamsStr}`)
    }

    // 請求データ更新
    async putFacilityReserveBillingData(billingDataId: number, putBillingData: PutBillingDataParams):
        Promise<NatPostFacilityReserveBillingData> {
        return await this.http.put(
            `/api/admin/facilityReserve/billingData/${billingDataId}`,
            putBillingData,
        )
    }

    // 請求データ出力
    async postFacilityReserveBillingData(postFacilityReserveBillingDataParams: NatPostFacilityReserveBillingDataParams):
        Promise<NatPostFacilityReserveBillingData> {
        return await this.http.post(
            `/api/admin/facilityReserve/billingData`,
            postFacilityReserveBillingDataParams,
            ContentType.JSON,
        )
    }

    // 施設予約一覧取得
    async getFacilityReserveList(searchCondition?: FacilityReserveSearchCondition,
                                 searchConditionPageInfo?: FacilityReserveSearchConditionPageInfo,
                                 searchConditionSortInfos?: SortOrder[],
    ): Promise<NatGetFacilityReserveList> {
        const searchParams: string[] = []
        if (searchCondition) {
            if (searchCondition.facilityId) {
                searchParams.push(`facilityId=${searchCondition.facilityId}`)
            }
            if (searchCondition.categoryId) {
                searchParams.push(`categoryId=${searchCondition.categoryId}`)
            }
            if (searchCondition.userId) {
                searchParams.push(`userId=${searchCondition.userId}`)
            }
            if (searchCondition.userRidgeCd) {
                searchParams.push(`userRidgeCd=${searchCondition.userRidgeCd}`)
            }
            if (searchCondition.userRoomNumber) {
                searchParams.push(`userRoomNumber=${searchCondition.userRoomNumber}`)
            }
            if (searchCondition.reserveStatusList) {
                searchParams.push(...searchCondition.reserveStatusList.map(status => `reserveStatus=${status}`))
            }
            if (searchCondition.reserveStartDate) {
                searchParams.push(`reserveStartDate=${searchCondition.reserveStartDate}`)
            }
            if (searchCondition.reserveEndDate) {
                searchParams.push(`reserveEndDate=${searchCondition.reserveEndDate}`)
            }
        }

        if (searchConditionPageInfo) {
            if (searchConditionPageInfo.pageNum) {
                searchParams.push(`pageNum=${searchConditionPageInfo.pageNum}`)
            }
            searchParams.push(`page=${searchConditionPageInfo.page}`)
        }

        if (searchConditionSortInfos) {
            searchConditionSortInfos.forEach((sortInfo) => {
                const value: string = encodeURIComponent(`${sortInfo.sortKey}:${sortInfo.sortDirection}`)
                searchParams.push(`sortInfo=${value}`)
            })
        }

        const searchParamsText: string = searchParams.join('&')
        return await this.http.get(`/api/admin/facilityReserve?${searchParamsText}`)
    }

    // 施設予約CSV出力
    getFacilityReserveDataOutput(
        request: NatGetFacilityReserveDataOutputParams): string {

        const searchParams: string[] = []
        if (request.facilityId) {
            searchParams.push(`facilityId=${request.facilityId}`)
        }
        if (request.categoryId) {
            searchParams.push(`categoryId=${request.categoryId}`)
        }
        if (request.userId) {
            searchParams.push(`userId=${request.userId}`)
        }
        if (request.userRidgeCd) {
            searchParams.push(`userRidgeCd=${request.userRidgeCd}`)
        }
        if (request.userRoomNumber) {
            searchParams.push(`userRoomNumber=${request.userRoomNumber}`)
        }
        if (request.reserveStatusList) {
            searchParams.push(...request.reserveStatusList.map(status => `reserveStatusList=${status}`))
        }
        if (request.reserveStartDate) {
            searchParams.push(`reserveStartDate=${request.reserveStartDate}`)
        }
        if (request.reserveEndDate) {
            searchParams.push(`reserveEndDate=${request.reserveEndDate}`)
        }

        const searchParamsText: string = searchParams.join('&')

        return `/api/admin/facilityReserve/outputCsv?${searchParamsText}`
    }

    // 追加課金一覧取得
    async getAdditionalBillingList(searchCondition?: SearchCondition,
                                   searchConditionPageInfo?: SearchConditionPageInfo,
                                   searchConditionSortInfos?: SortOrder[],
    ): Promise<NatGetAdditionalBillingList> {
        const searchParams: string[] = []
        if (searchCondition) {
            if (searchCondition.userId) {
                searchParams.push(`userId=${searchCondition.userId}`)
            }
            if (searchCondition.userRidgeCd) {
                searchParams.push(`userRidgeCd=${searchCondition.userRidgeCd}`)
            }
            if (searchCondition.userRoomNumber) {
                searchParams.push(`userRoomNumber=${searchCondition.userRoomNumber}`)
            }
            if (searchCondition.reserveStartDate) {
                searchParams.push(`reserveStartDate=${searchCondition.reserveStartDate}`)
            }
            if (searchCondition.reserveEndDate) {
                searchParams.push(`reserveEndDate=${searchCondition.reserveEndDate}`)
            }
        }

        if (searchConditionPageInfo) {
            if (searchConditionPageInfo.pageNum) {
                searchParams.push(`pageNum=${searchConditionPageInfo.pageNum}`)
            }
            searchParams.push(`page=${searchConditionPageInfo.page}`)
        }

        if (searchConditionSortInfos) {
            searchConditionSortInfos.forEach((sortInfo) => {
                const value: string = encodeURIComponent(`${sortInfo.sortKey}:${sortInfo.sortDirection}`)
                searchParams.push(`sortInfo=${value}`)
            })
        }

        const searchParamsText: string = searchParams.join('&')
        return await this.http.get(`/api/admin/facilityReserve/additionalCharge?${searchParamsText}`)
    }

    // 施設予約取得
    async getFacilityForReservation(
        condominiumCd: string, facilityId: string): Promise<FacilityForReservation> {
        const responseJson: ApiResponse<FacilityForReservation> =
            await this.http.get(`/api/admin/condominiums/${condominiumCd}/facilities/${facilityId}/facilityForReservation`)
        const apiResponse: ApiResponse<FacilityForReservation> = ApiResponse.from(
            responseJson,
            FacilityForReservation.from,
        )
        return apiResponse.details
    }

    // 施設予約取得
    async getFacilityReserve(
        condominiumCd: string, facilityId: string, facilityReserveId: string): Promise<NatGetFacilityReserve> {
        return await this.http.get(`/api/admin/condominiums/${condominiumCd}/facilities/${facilityId}/reservations/${facilityReserveId}`)
    }

    // 施設予約削除
    async deleteFacilityReserve(facilityReserveId: string, version: number): Promise<NatDeleteFacilityReserve> {
        return await this.http.delete(`/api/admin/facilityReserve/${facilityReserveId}?version=${version}`)
    }

    // 施設予約更新（時間貸）
    async putUpdateFacilityReserveDuration(facilityReserveId: string, request: NatPutFacilityReserveDurationRequest):
        Promise<NatPutFacilityReserveDuration> {
        return await
            this.http.put(`/api/admin/facilityReserve/reservations-for-duration/${facilityReserveId}`, request)
    }

    // 施設予約更新（宿泊）
    async putUpdateFacilityReserveOvernight(facilityReserveId: string, request: NatPutFacilityReserveOvernightRequest):
        Promise<NatPutFacilityReserveOvernight> {
        return await
            this.http.put(`/api/admin/facilityReserve/reservations-for-overnight/${facilityReserveId}`, request)
    }

    // 追加課金取得
    async getAdditionalBilling(facilityReserveId: string): Promise<NatGetAdditionalBilling> {
        return await this.http.get(`/api/admin/facilityReserve/additionalCharge/${facilityReserveId}`)
    }

    // 追加課金削除
    async deleteAdditionalBilling(facilityReserveId: string): Promise<NatDeleteAdditionalBilling> {
        return await this.http.delete(`/api/admin/facilityReserve/additionalCharge/${facilityReserveId}`)
    }

    // 追加課金登録
    async postAdditionalBilling(request: NatPostAdditionalBillingParams): Promise<NatPostAdditionalBilling> {
        return await this.http.post(`/api/admin/facilityReserve/additionalCharge`, {
                reserveTitle: request.reserveTitle,
                facilityId: request.facilityId,
                reserveStartDate: request.reserveStartDate,
                userId: request.userId,
                userRidgeCd: request.userRidgeCd,
                userRoomNumber: request.userRoomNumber,
                facilityUseCharge: request.facilityUseCharge,
                paymentMethod: request.paymentMethod,
                cashPaymentStatus: request.cashPaymentStatus,
            }, ContentType.JSON,
        )
    }

    // 追加課金更新
    async putUpdateAdditionalBilling(request: NatPutAdditionalBillingParams): Promise<NatPutAdditionalBilling> {
        return await this.http.put(`/api/admin/facilityReserve/additionalCharge/${request.facilityReserveId}`, request)
    }

    // あなたの予約取得(未来の「確定」予約)
    async getFutureReservationsLimit(
        condominiumCd: string,
        limit: number): Promise<ReservationStatusCardInformation[]> {
        const response: ApiResponse<ReservationStatusCardInformation[]> =
            await this.http.get(
                `/api/condominiums/${condominiumCd}/reservations?filter=future-election-only&limit=${limit}`,
            )
        const apiResponse: ApiResponse<ReservationCardInformation[]> =
            ApiResponse.fromArray(response, ReservationCardInformation.from)
        return apiResponse.details
    }

    async cancelReservations(
        closedSettingParam: NatPostClosedSettingParams,
    ): Promise<boolean> {
        await this.http.put(
            '/api/admin/facilityReserve/cancelReservations',
            {
                registerClosedSetting: closedSettingParam.registerClosedSetting,
                updateClosedSetting: closedSettingParam.updateClosedSetting,
                deleteClosedSetting: closedSettingParam.deleteClosedSetting,
            },
        )
        return true
    }

    async getFacilityReservationsByClosedPeriod(
        closeSetting: RegisterClosedSetting | UpdateClosedSetting,
    ): Promise<ReservationsByClosedPeriod[] | null> {
        const response: ApiResponse<ReservationsByClosedPeriod[]> = await this.http.get(
            `/api/admin/facilityReserve/closedPeriod`
            + `?facilityId=${closeSetting.facilityId}`
            + `&startDate=${closeSetting.startDate}`
            + `&startTimeHour=${closeSetting.startTimeHour}`
            + `&startTimeMinute=${closeSetting.startTimeMinute}`
            + `&endDate=${closeSetting.endDate}`
            + `&endTimeHour=${closeSetting.endTimeHour}`
            + `&endTimeMinute=${closeSetting.endTimeMinute}`,
        )

        if (!response || !response.details) {
            return null
        }
        return response.details.map((detail: ReservationsByClosedPeriod) => {
            return {
                facilityReserveId: detail.facilityReserveId,
                facilityReserveCategory: detail.facilityReserveCategory,
                reserveTitle: detail.reserveTitle,
                startDateTime: moment(detail.startDateTime),
                endDateTime: moment(detail.endDateTime),
                userId: detail.userId,
                userRidgeCd: detail.userRidgeCd,
                userRidgeName: detail.userRidgeName,
                userRoomNumber: detail.userRoomNumber,
                userFamilyName: detail.userFamilyName,
                userFirstName: detail.userFirstName,
            }
        })
    }
}
